// Stability Anchor: 2026-05-03 - Force Fresh Production Build
import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/useData';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import Landing from '../pages/Landing';
import { lazyWithRetry } from '../utils/loadUtils';
import { Loader } from 'lucide-react';
import BrandSplashLoader from './BrandSplashLoader';

const MainAppWithProviders = lazyWithRetry(() => import('./MainApp'));
const TermsOfService = lazyWithRetry(() => import('../pages/TermsOfService'));
const FreeHRSoftwareUK = lazyWithRetry(() => import('../pages/FreeHRSoftwareUK'));
const ClockifyAlternativeUK = lazyWithRetry(() => import('../pages/ClockifyAlternativeUK'));
const BreatheHRAlternativeUK = lazyWithRetry(() => import('../pages/BreatheHRAlternativeUK'));
const DeputyAlternativeUK = lazyWithRetry(() => import('../pages/DeputyAlternativeUK'));
const ConnecteamAlternativeUK = lazyWithRetry(() => import('../pages/ConnecteamAlternativeUK'));
const About = lazyWithRetry(() => import('../pages/About'));
const PinTimeClock = lazyWithRetry(() => import('../pages/PinTimeClock'));
const TabletTimeClockKiosk = lazyWithRetry(() => import('../pages/TabletTimeClockKiosk'));
const NfcCardClockIn = lazyWithRetry(() => import('../pages/NfcCardClockIn'));
const RefundPolicy = lazyWithRetry(() => import('../pages/RefundPolicy'));
const Resources = lazyWithRetry(() => import('../pages/Resources'));
const ArticleUkHolidayAccrual = lazyWithRetry(() => import('../pages/ArticleUkHolidayAccrual'));
const ArticleStopBuddyPunching = lazyWithRetry(() => import('../pages/ArticleStopBuddyPunching'));
const ArticlePaperVsDigitalTimesheets = lazyWithRetry(() => import('../pages/ArticlePaperVsDigitalTimesheets'));
const WorkingTimeRegulationsUkGuide = lazyWithRetry(() => import('../pages/WorkingTimeRegulationsUkGuide'));
const DayOneSspHmrcRules = lazyWithRetry(() => import('../pages/DayOneSspHmrcRules'));
const NfcDeskPlaqueClockInGuide = lazyWithRetry(() => import('../pages/NfcDeskPlaqueClockInGuide'));
const Calculate1207HolidayAccrual = lazyWithRetry(() => import('../pages/Calculate1207HolidayAccrual'));
const StopBuddyPunchingUk = lazyWithRetry(() => import('../pages/StopBuddyPunchingUk'));
const TabletKioskVsMobileGpsClockIn = lazyWithRetry(() => import('../pages/TabletKioskVsMobileGpsClockIn'));
const BuildEmployeeShiftRotaUk = lazyWithRetry(() => import('../pages/BuildEmployeeShiftRotaUk'));
const AutomatedPayrollTimesheetsUk = lazyWithRetry(() => import('../pages/AutomatedPayrollTimesheetsUk'));
const Legal = lazyWithRetry(() => import('../pages/Legal'));

const LoadingFallback = () => <BrandSplashLoader />;

const LandingWithRedirect: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const isKioskMode = localStorage.getItem('app_mode') === 'kiosk';
    const isEmployee = localStorage.getItem('employeeToken');
    if (isKioskMode || isEmployee) {
      navigate('/kiosk', { replace: true });
    }
  }, [navigate]);
  return <Landing />;
};

const LandingWithPricingScroll: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const isKioskMode = localStorage.getItem('app_mode') === 'kiosk';
    const isEmployee = localStorage.getItem('employeeToken');
    if (isKioskMode || isEmployee) {
      navigate('/kiosk', { replace: true });
      return;
    }
    const timer = setTimeout(() => {
      const el = document.getElementById('pricing');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [navigate]);
  return <Landing />;
};

const XeroBridge: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    if (code) {
      // PRO-FIX: Immediately save and move to the protected app route.
      localStorage.setItem('xero_handoff', JSON.stringify({ code, state, timestamp: Date.now() }));
      
      // FORCE REDIRECT: We use a hard navigate to ensure we stay within the /app/ context
      navigate(`/app/reports?code=${code}${state ? `&state=${state}` : ''}`, { replace: true });
    } else {
      // No code? Just go to the standard app reports page.
      navigate('/app/reports', { replace: true });
    }
  }, [navigate, location.search]);

  return <LoadingFallback />;
};

const App: React.FC = () => {
  const isNative = Capacitor.isNativePlatform();
  const navigate = useNavigate();
  const location = useLocation();

  const { loading: authLoading } = useAuth();
  const { isLoading: dataLoading } = useData();

  const hasSplashDismissed = React.useRef(false);
  const appStartTimeRef = React.useRef(Date.now());

  useEffect(() => {
    // Only run splash coordination on actual native Capacitor apps
    const isActuallyNativeApp = Boolean(
      (window as any).Capacitor && 
      typeof (window as any).Capacitor.isNativePlatform === 'function' && 
      (window as any).Capacitor.isNativePlatform()
    ) || 
    window.location.protocol === 'file:' || 
    window.location.origin.indexOf('capacitor://') !== -1 ||
    window.location.origin.indexOf('ionic://') !== -1;

    if (!isActuallyNativeApp) {
      // On web (desktop and mobile web browsers like mobile Chrome/Safari),
      // hide loader immediately on mount and ensure content is visible & scrollable
      const loader = document.getElementById('initial-loader');
      if (loader) loader.style.setProperty('display', 'none', 'important');
      const rootEl = document.getElementById('root');
      if (rootEl) rootEl.style.setProperty('display', 'block', 'important');
      document.documentElement.classList.remove('is-native-app');
      document.documentElement.classList.remove('is-android');
      return;
    }

    if (hasSplashDismissed.current) return;

    const MIN_SPLASH_TIME = 2000; // 2.0s display duration for full Lottie logo reveal
    const MAX_SPLASH_TIME = 4500; // Safety cap so splash screen never hangs

    const dismissSplash = () => {
      if (hasSplashDismissed.current) return;
      hasSplashDismissed.current = true;

      const loader = document.getElementById('initial-loader');
      if (loader) {
        loader.style.transition = 'opacity 0.4s ease-out';
        loader.style.opacity = '0';
        setTimeout(() => {
          loader.style.setProperty('display', 'none', 'important');
        }, 400);
      }
      const rootEl = document.getElementById('root');
      if (rootEl) {
        rootEl.style.setProperty('display', 'block', 'important');
      }
      document.documentElement.classList.remove('is-native-app');
    };

    const maxTimer = setTimeout(dismissSplash, MAX_SPLASH_TIME);

    if (!authLoading && !dataLoading) {
      const elapsed = Date.now() - appStartTimeRef.current;
      const remaining = Math.max(0, MIN_SPLASH_TIME - elapsed);

      const timer = setTimeout(dismissSplash, remaining);
      return () => {
        clearTimeout(timer);
        clearTimeout(maxTimer);
      };
    }

    return () => clearTimeout(maxTimer);
  }, [authLoading, dataLoading]);

  useEffect(() => {
    if (!isNative) return;

    // Handle Android hardware back button
    const backButtonListener = CapApp.addListener('backButton', () => {
      const currentPath = location.pathname;
      
      // If we are at entry points or main dashboard screen, exit app
      if (currentPath === '/login' || currentPath === '/kiosk' || currentPath === '/' || currentPath === '/app/dashboard') {
        CapApp.exitApp();
      } else {
        navigate(-1);
      }
    });

    // Re-play branded splash animation on native app resume from background
    const appStateListener = CapApp.addListener('appStateChange', (state) => {
      if (state.isActive) {
        if (typeof (window as any).triggerSplashAnimation === 'function') {
          (window as any).triggerSplashAnimation(1500);
        }
      }
    });

    return () => {
      backButtonListener.then(h => h.remove());
      appStateListener.then(h => h.remove());
    };
  }, [isNative, navigate, location]);

  return (
    <Routes>
      <Route path="/" element={isNative ? <Navigate to="/login" replace /> : <LandingWithRedirect />} />
      <Route path="/pricing" element={isNative ? <Navigate to="/login" replace /> : <LandingWithPricingScroll />} />
      <Route path="/pricing/" element={isNative ? <Navigate to="/login" replace /> : <LandingWithPricingScroll />} />
      <Route path="/reports" element={<XeroBridge />} />
      <Route path="/terms" element={
        <Suspense fallback={<LoadingFallback />}>
          <TermsOfService />
        </Suspense>
      } />
      <Route path="/privacy" element={
        <Suspense fallback={<LoadingFallback />}>
          <Legal />
        </Suspense>
      } />
      <Route path="/privacy/" element={
        <Suspense fallback={<LoadingFallback />}>
          <Legal />
        </Suspense>
      } />
      <Route path="/privacy-policy" element={
        <Suspense fallback={<LoadingFallback />}>
          <Legal />
        </Suspense>
      } />
      <Route path="/privacy-policy/" element={
        <Suspense fallback={<LoadingFallback />}>
          <Legal />
        </Suspense>
      } />
      <Route path="/about" element={
        <Suspense fallback={<LoadingFallback />}>
          <About />
        </Suspense>
      } />
      <Route path="/free-hr-software-uk" element={
        <Suspense fallback={<LoadingFallback />}>
          <FreeHRSoftwareUK />
        </Suspense>
      } />
      <Route path="/clockify-alternative-uk" element={
        <Suspense fallback={<LoadingFallback />}>
          <ClockifyAlternativeUK />
        </Suspense>
      } />
      <Route path="/breathe-hr-alternative-uk" element={
        <Suspense fallback={<LoadingFallback />}>
          <BreatheHRAlternativeUK />
        </Suspense>
      } />
      <Route path="/deputy-alternative-uk" element={
        <Suspense fallback={<LoadingFallback />}>
          <DeputyAlternativeUK />
        </Suspense>
      } />
      <Route path="/connecteam-alternative-uk" element={
        <Suspense fallback={<LoadingFallback />}>
          <ConnecteamAlternativeUK />
        </Suspense>
      } />
      <Route path="/connecteam-alternative-uk/" element={
        <Suspense fallback={<LoadingFallback />}>
          <ConnecteamAlternativeUK />
        </Suspense>
      } />
      <Route path="/pin-time-clock" element={
        <Suspense fallback={<LoadingFallback />}>
          <PinTimeClock />
        </Suspense>
      } />
      <Route path="/pin-time-clock/" element={
        <Suspense fallback={<LoadingFallback />}>
          <PinTimeClock />
        </Suspense>
      } />
      <Route path="/tablet-time-clock-kiosk" element={
        <Suspense fallback={<LoadingFallback />}>
          <TabletTimeClockKiosk />
        </Suspense>
      } />
      <Route path="/tablet-time-clock-kiosk/" element={
        <Suspense fallback={<LoadingFallback />}>
          <TabletTimeClockKiosk />
        </Suspense>
      } />
      <Route path="/nfc-card-clock-in" element={
        <Suspense fallback={<LoadingFallback />}>
          <NfcCardClockIn />
        </Suspense>
      } />
      <Route path="/nfc-card-clock-in/" element={
        <Suspense fallback={<LoadingFallback />}>
          <NfcCardClockIn />
        </Suspense>
      } />
      <Route path="/refund-policy" element={
        <Suspense fallback={<LoadingFallback />}>
          <RefundPolicy />
        </Suspense>
      } />
      <Route path="/refund-policy/" element={
        <Suspense fallback={<LoadingFallback />}>
          <RefundPolicy />
        </Suspense>
      } />
      <Route path="/resources" element={
        <Suspense fallback={<LoadingFallback />}>
          <Resources />
        </Suspense>
      } />
      <Route path="/resources/" element={
        <Suspense fallback={<LoadingFallback />}>
          <Resources />
        </Suspense>
      } />
      <Route path="/resources/uk-holiday-entitlement-irregular-hours" element={
        <Suspense fallback={<LoadingFallback />}>
          <ArticleUkHolidayAccrual />
        </Suspense>
      } />
      <Route path="/resources/uk-holiday-entitlement-irregular-hours/" element={
        <Suspense fallback={<LoadingFallback />}>
          <ArticleUkHolidayAccrual />
        </Suspense>
      } />
      <Route path="/resources/stop-buddy-punching-without-expensive-hardware" element={
        <Suspense fallback={<LoadingFallback />}>
          <ArticleStopBuddyPunching />
        </Suspense>
      } />
      <Route path="/resources/stop-buddy-punching-without-expensive-hardware/" element={
        <Suspense fallback={<LoadingFallback />}>
          <ArticleStopBuddyPunching />
        </Suspense>
      } />
      <Route path="/resources/paper-timesheets-vs-digital-time-tracking" element={
        <Suspense fallback={<LoadingFallback />}>
          <ArticlePaperVsDigitalTimesheets />
        </Suspense>
      } />
      <Route path="/resources/paper-timesheets-vs-digital-time-tracking/" element={
        <Suspense fallback={<LoadingFallback />}>
          <ArticlePaperVsDigitalTimesheets />
        </Suspense>
      } />
      <Route path="/resources/working-time-regulations-uk-guide" element={
        <Suspense fallback={<LoadingFallback />}>
          <WorkingTimeRegulationsUkGuide />
        </Suspense>
      } />
      <Route path="/resources/working-time-regulations-uk-guide/" element={
        <Suspense fallback={<LoadingFallback />}>
          <WorkingTimeRegulationsUkGuide />
        </Suspense>
      } />
      <Route path="/resources/day-one-ssp-hmrc-rules" element={
        <Suspense fallback={<LoadingFallback />}>
          <DayOneSspHmrcRules />
        </Suspense>
      } />
      <Route path="/resources/day-one-ssp-hmrc-rules/" element={
        <Suspense fallback={<LoadingFallback />}>
          <DayOneSspHmrcRules />
        </Suspense>
      } />
      <Route path="/resources/nfc-desk-plaque-clock-in-guide" element={
        <Suspense fallback={<LoadingFallback />}>
          <NfcDeskPlaqueClockInGuide />
        </Suspense>
      } />
      <Route path="/resources/nfc-desk-plaque-clock-in-guide/" element={
        <Suspense fallback={<LoadingFallback />}>
          <NfcDeskPlaqueClockInGuide />
        </Suspense>
      } />
      <Route path="/resources/calculate-12-07-holiday-accrual" element={
        <Suspense fallback={<LoadingFallback />}>
          <Calculate1207HolidayAccrual />
        </Suspense>
      } />
      <Route path="/resources/calculate-12-07-holiday-accrual/" element={
        <Suspense fallback={<LoadingFallback />}>
          <Calculate1207HolidayAccrual />
        </Suspense>
      } />
      <Route path="/resources/stop-buddy-punching-uk" element={
        <Suspense fallback={<LoadingFallback />}>
          <StopBuddyPunchingUk />
        </Suspense>
      } />
      <Route path="/resources/stop-buddy-punching-uk/" element={
        <Suspense fallback={<LoadingFallback />}>
          <StopBuddyPunchingUk />
        </Suspense>
      } />
      <Route path="/resources/tablet-kiosk-vs-mobile-gps-clock-in" element={
        <Suspense fallback={<LoadingFallback />}>
          <TabletKioskVsMobileGpsClockIn />
        </Suspense>
      } />
      <Route path="/resources/tablet-kiosk-vs-mobile-gps-clock-in/" element={
        <Suspense fallback={<LoadingFallback />}>
          <TabletKioskVsMobileGpsClockIn />
        </Suspense>
      } />
      <Route path="/resources/build-employee-shift-rota-uk" element={
        <Suspense fallback={<LoadingFallback />}>
          <BuildEmployeeShiftRotaUk />
        </Suspense>
      } />
      <Route path="/resources/build-employee-shift-rota-uk/" element={
        <Suspense fallback={<LoadingFallback />}>
          <BuildEmployeeShiftRotaUk />
        </Suspense>
      } />
      <Route path="/resources/automated-payroll-timesheets-uk" element={
        <Suspense fallback={<LoadingFallback />}>
          <AutomatedPayrollTimesheetsUk />
        </Suspense>
      } />
      <Route path="/resources/automated-payroll-timesheets-uk/" element={
        <Suspense fallback={<LoadingFallback />}>
          <AutomatedPayrollTimesheetsUk />
        </Suspense>
      } />
      <Route path="/*" element={
        <Suspense fallback={<LoadingFallback />}>
          <MainAppWithProviders />
        </Suspense>
      } />
    </Routes>
  );
};

export default App;
